import styled from '@emotion/styled';
import { useMemo, useState } from 'react';

import { HomeyApiKind } from '../../../lib/HomeyApiKind';
import * as Containers from '../Containers';
import { Option, Select } from '../Select';

import { HomeyUtils } from '../../../store/HomeyUtils';

import { useDevice } from '../../../store/devices/useDevices';
import { useBaseUrl, useApi } from '../../../store/SelectedHomeyStore.hooks';

import type { SnapshotImageData } from './getSnapshotImageUrl';
import { LiveVideoView } from './LiveVideoView';
import { SnapshotView } from './SnapshotView';

const SOURCE_TYPE = {
  SNAPSHOT: 'snapshot',
  LIVE: 'live',
} as const;

interface LiveSource {
  id: string;
  type: typeof SOURCE_TYPE.LIVE;
  title: string;
  videoId?: string;
  deviceVideoId: string;
}

interface SnapshotSource {
  id: string;
  type: typeof SOURCE_TYPE.SNAPSHOT;
  title: string;
  image?: SnapshotImageData;
  imageId?: string;
}

type CameraSource = LiveSource | SnapshotSource;

export interface CameraProps {
  deviceId: string;
}

export function Camera(props: CameraProps) {
  const deviceSelection = useDevice({ deviceId: props.deviceId });
  const { device } = deviceSelection;
  const { baseUrl } = useBaseUrl();
  const { homey } = useApi();

  const hasVideoStreaming = HomeyUtils.getHasVideoStreaming(homey);
  const deviceImages = device?.images;
  const deviceVideos =
    deviceSelection.apiKind === HomeyApiKind.V3_LOCAL ? deviceSelection.device?.videos : undefined;

  // Build combined list of camera sources (snapshots and live videos)
  const cameraSources = useMemo<CameraSource[]>(() => {
    const sources: CameraSource[] = [];

    // Add live video sources first (preferred) - only if Homey supports video streaming
    if (hasVideoStreaming === true) {
      for (const video of deviceVideos ?? []) {
        if (video.type !== 'camera') {
          continue;
        }

        sources.push({
          id: `live:${video.videoObj?.id}`,
          type: SOURCE_TYPE.LIVE,
          title: video.title ?? video.id,
          videoId: video.videoObj?.id,
          deviceVideoId: video.id,
        });
      }
    }

    // Add snapshot sources
    for (const image of deviceImages ?? []) {
      if (image.type !== 'camera') {
        continue;
      }

      sources.push({
        id: `snapshot:${image.imageObj?.id}`,
        type: SOURCE_TYPE.SNAPSHOT,
        title: image.title ?? image.id,
        image: image.imageObj ?? undefined,
        imageId: image.imageObj?.id,
      });
    }

    return sources;
  }, [deviceImages, deviceVideos, hasVideoStreaming]);

  // Track user's selection (may become invalid when sources change)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  // Derive valid selection - fall back to first source if current selection is invalid
  const isSelectionValid = cameraSources.some(
    (cameraSource) => cameraSource.id === selectedSourceId,
  );
  const effectiveSourceId = isSelectionValid ? selectedSourceId : (cameraSources[0]?.id ?? null);
  const selectedSource = cameraSources.find(
    (cameraSource) => cameraSource.id === effectiveSourceId,
  );

  return (
    <Containers.Control>
      <Containers.Select>
        {cameraSources.length > 1 && (
          <S.SelectWrapper>
            <Select
              aria-label="Camera Select"
              // @ts-expect-error Select component types
              selectedKey={effectiveSourceId}
              onSelectionChange={(key: string | null) => {
                setSelectedSourceId(key);
              }}
            >
              {cameraSources.map((source) => (
                <Option key={source.id}>{source.title}</Option>
              ))}
            </Select>
          </S.SelectWrapper>
        )}
      </Containers.Select>

      <Containers.Action>
        {selectedSource?.type === SOURCE_TYPE.LIVE ? (
          <LiveVideoView device={device} baseUrl={baseUrl} source={selectedSource} />
        ) : selectedSource?.type === SOURCE_TYPE.SNAPSHOT ? (
          <SnapshotView baseUrl={baseUrl} source={selectedSource} />
        ) : null}
      </Containers.Action>
    </Containers.Control>
  );
}

function S() {}
Camera.S = S;

S.SelectWrapper = styled.div`
  button > div:first-of-type {
    text-transform: none;
  }
`;
